import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';

import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { irTypesConst } from 'app/integrationruntime/integrationruntime.constants';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ManagedVirtualNetwork, Credential, SharedModel, NamedAsyncAdapter } from 'shell-models-lib';
import * as ssisConstants from 'app/integrationruntime/ir-authoring/ir-ssis/ssis.constants';
import { stringIsNullOrEmpty } from '../../../shared/utils/string.util';
import { ResourceId } from 'app/shared/utils/resource-id';
import { formatRegionString } from 'app/shared/utils/region.util';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { SelfHostedIntegrationRuntime } from 'app/model/integrationRuntime/selfhosted.integrationRuntime';
import { ComputeType } from 'app/shared/constants/dataflow-runtime.options';
import { CmdkeyCommandSetup } from 'app/model/expressCustomSetup/cmdKeySetup';
import { EnvironmentVariableSetup } from 'app/model/expressCustomSetup/environmentVariableSetup';
import { expressCustomSetupTypes } from 'app/model/expressCustomSetup/expressCustomSetup';
import { isLicensedComponent, ComponentSetup, supportedComponentNames } from 'app/model/expressCustomSetup/componentSetup';
import { LicensedComponentSetup } from 'app/model/expressCustomSetup/licensedComponentSetup';
import { isAvailableComponent } from 'app/model/integrationRuntime/integrationRuntime-ssis.utils';
import { PackageStore } from 'app/model/packageStore/packageStore';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { ReferenceAdapter } from '../reference.adapter';
import { AzPowerShellSetup } from '../../expressCustomSetup/azPowerShellSetup';
import { GlobalVariables } from 'app/services/globals';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ManagedIntegrationRuntime),
    asynchronous: true
})
export class ManagedIntegrationRuntimeAdapter extends NamedAsyncAdapter<ManagedIntegrationRuntime, Model.IntegrationRuntimeResource> {

    public model(): IModel {
        return Metamodel.modelFor(ManagedIntegrationRuntime);
    }

    protected async _toResource(ir: ManagedIntegrationRuntime): Promise<Model.IntegrationRuntimeResource> {
        const modelIR = await super._toResource(ir);

        const modelIRComputeVNetProperties: Model.IntegrationRuntimeVNetProperties | undefined =
            await this._toModelIRComputeVNetProperties(ir);

        let modelIRComputeVirtualNetwork: Model.IntegrationRuntimeVirtualNetwork | undefined;
        if (ir.computeSubnetResourceId != null) {
            modelIRComputeVirtualNetwork = {
                subnetId: ir.computeSubnetResourceId
            };
        }

        let modelIRComputeProperties: Model.IntegrationRuntimeComputeProperties | undefined;
        if (ir.computeLocation != null || ir.computeNodeSize != null || ir.computeNumberOfNodes != null || ir.computeMaxParallelExecutionsPerNode != null ||
            modelIRComputeVNetProperties != null || modelIRComputeVirtualNetwork != null) {
            modelIRComputeProperties = {
                location: ir.computeLocation,
                nodeSize: ir.computeNodeSize,
                numberOfNodes: ir.computeNumberOfNodes,
                maxParallelExecutionsPerNode: ir.computeMaxParallelExecutionsPerNode,
                vNetProperties: modelIRComputeVNetProperties,
                virtualNetwork: modelIRComputeVirtualNetwork
            };
        }

        let modelIRCustomerVirtualNetwork: Model.IntegrationRuntimeCustomerVirtualNetwork | undefined;
        const ecSvc = await getServiceInstance<ExposureControlService>(this.context.contextData, 'ExposureControlService');
        if (ecSvc.enableSsisIrVNetV2Feature && ir.useVNetV2 && ir.computeVNetId != null && ir.computeSubnet != null) {
            modelIRCustomerVirtualNetwork = {
                subnetId: ir.computeVNetId + '/subnets/' + ir.computeSubnet
            };
        }

        let modelIRSSISCatalogInfo: Model.IntegrationRuntimeSsisCatalogInfo | undefined;
        if (ir.ssisCatalogServerEndpoint != null || ir.ssisCatalogAdminUsername != null || ir.ssisCatalogAdminPassword != null || ir.ssisCatalogPricingTier != null) {
            modelIRSSISCatalogInfo = {
                catalogServerEndpoint: ir.ssisCatalogServerEndpoint,
                catalogAdminUserName: ir.ssisCatalogAdminUsername ? ir.ssisCatalogAdminUsername : undefined,
                catalogAdminPassword: ir.ssisCatalogAdminPassword == null ? undefined : {
                    type: 'SecureString',
                    value: ir.ssisCatalogAdminPassword
                },
                dualStandbyPairName: ir.dualStandbyPairName,
                catalogPricingTier: ir.ssisCatalogPricingTier
            };
        }

        let modelIRCredential: Model.CredentialReference | undefined;
        if (ir.credential != null) {
            modelIRCredential = {
                type: 'CredentialReference',
                referenceName: ir.credential.name
            };
        }

        let modelIRPackageStores: Model.PackageStore[] | undefined;
        if (ir.usePackageStore && ir.packageStores != null && ir.packageStores.length > 0) {
            modelIRPackageStores = [];
            ir.packageStores.forEach((store: PackageStore) => {
                const packageStore: Model.PackageStore = {
                    name: store.storeName,
                    packageStoreLinkedService: store.linkedService ? linkedServiceReferenceToResource(store.linkedService, this.context,
                        this.ensureLogger()) : undefined
                };
                modelIRPackageStores.push(packageStore);
            });
        }

        let modelIRCustomSetupScriptProperties: Model.IntegrationRuntimeCustomSetupScriptProperties | undefined;
        if (ir.ssisCustomSetupContainerSasUri != null && ir.ssisCustomSetupContainerSasUri !== '') {
            let index = ir.ssisCustomSetupContainerSasUri.indexOf('?');
            if (index < 0) {
                index = ir.ssisCustomSetupContainerSasUri.indexOf('*');
            }
            modelIRCustomSetupScriptProperties = {
                blobContainerUri: index >= 0 ? ir.ssisCustomSetupContainerSasUri.substring(0, index) : ir.ssisCustomSetupContainerSasUri,
                sasToken: index >= 0 ? {
                    type: 'SecureString',
                    value: ir.ssisCustomSetupContainerSasUri.substring(index)
                } : undefined
            };
        }

        let modelIRSelfHostedIR: Model.IntegrationRuntimeReference | undefined;
        if (ir.ssisSelfHostedIR != null) {
            modelIRSelfHostedIR = {
                type: 'IntegrationRuntimeReference',
                referenceName: ir.ssisSelfHostedIR.name
            };
        }

        let modelIRBlobLinkedService: Model.LinkedServiceReference | undefined;
        if (ir.ssisBlobLinkedService != null) {
            modelIRBlobLinkedService = {
                type: 'LinkedServiceReference',
                referenceName: ir.ssisBlobLinkedService.name
            };
        }

        let modelIRDataProxyProperties: Model.IntegrationRuntimeSsisDataProxyProperties | undefined;
        if (modelIRSelfHostedIR != null || modelIRBlobLinkedService != null) {
            modelIRDataProxyProperties = {
                connectVia: modelIRSelfHostedIR,
                stagingLinkedService: modelIRBlobLinkedService,
                path: ir.ssisStoragePath ? ir.ssisStoragePath : undefined
            };
        }

        let modelIRCustomSetups: Model.CustomSetupBase[] | undefined;
        if (ir.ssisCustomSetups != null && ir.ssisCustomSetups.length > 0) {
            modelIRCustomSetups = [];
            ir.ssisCustomSetups.forEach((setup) => {
                if (setup instanceof CmdkeyCommandSetup) {
                    const customSetup = setup as CmdkeyCommandSetup;
                    const modelIRCmdkeySetupTypeProperties: Model.CmdkeySetupTypeProperties = {
                        targetName: customSetup.targetName,
                        userName: customSetup.userName,
                        password: {
                            type: 'SecureString',
                            value: customSetup.password
                        }
                    };
                    const modelIRCmdkeySetup: Model.CmdkeySetup = {
                        type: expressCustomSetupTypes.Cmdkey,
                        typeProperties: modelIRCmdkeySetupTypeProperties
                    };
                    modelIRCustomSetups.push(modelIRCmdkeySetup);
                } else if (setup instanceof EnvironmentVariableSetup) {
                    const customSetup = setup as EnvironmentVariableSetup;
                    const modelIREnvironmentVariableSetupTypeProperties: Model.EnvironmentVariableSetupTypeProperties = {
                        variableName: customSetup.variableName,
                        variableValue: customSetup.variableValue
                    };
                    const modelIREnvironmentVariableSetup: Model.EnvironmentVariableSetup = {
                        type: expressCustomSetupTypes.EnvVariable,
                        typeProperties: modelIREnvironmentVariableSetupTypeProperties
                    };
                    modelIRCustomSetups.push(modelIREnvironmentVariableSetup);
                } else if (setup instanceof ComponentSetup) {
                    const customSetup = setup as ComponentSetup;
                    if (isLicensedComponent(customSetup.componentName)) {
                        const licensedComponentSetup = customSetup as LicensedComponentSetup;
                        const modelLicensedComponentSetupTypeProperties: Model.LicensedComponentSetupTypeProperties = {
                            componentName: licensedComponentSetup.componentName,
                            licenseKey: {
                                type: 'SecureString',
                                value: licensedComponentSetup.licenseKey
                            }
                        };
                        const modelIRLicensedComponentSetup: Model.LicensedComponentSetup = {
                            type: expressCustomSetupTypes.Installation,
                            typeProperties: modelLicensedComponentSetupTypeProperties
                        };
                        modelIRCustomSetups.push(modelIRLicensedComponentSetup);
                    } else {  // Non licensed component setup
                        const modelComponentSetupTypeProperties: Model.ComponentSetupTypeProperties = {
                            componentName: customSetup.componentName
                        };
                        const modelIRComponentSetup: Model.ComponentSetup = {
                            type: expressCustomSetupTypes.Installation,
                            typeProperties: modelComponentSetupTypeProperties
                        };
                        modelIRCustomSetups.push(modelIRComponentSetup);
                    }
                } else if (setup instanceof AzPowerShellSetup) {
                    const customSetup = setup as AzPowerShellSetup;
                    const modelIRAzPowerShellSetupTypeProperties: Model.AzPowerShellSetupTypeProperties = {
                        version: customSetup.version
                    };
                    const modelIRAzPowerShellSetup: Model.AzPowerShellSetup = {
                        type: expressCustomSetupTypes.AzPowerShell,
                        typeProperties: modelIRAzPowerShellSetupTypeProperties
                    };
                    modelIRCustomSetups.push(modelIRAzPowerShellSetup);
                }
            });
        }

        let modelIRSSISProperties: Model.IntegrationRuntimeSsisProperties | undefined;
        // convert UI custom property model to backend
        const customPropertiesLocal: Model.ICustomProperty[] = ir.customProperties != null ? ir.customProperties.map((prop) => { return { name: prop.key, value: prop.value }; }) : undefined;
        if (modelIRSSISCatalogInfo != null || ir.licenseType != null || modelIRCustomSetupScriptProperties != null || ir.skuSetting != null || modelIRDataProxyProperties != null
            || modelIRCustomSetups != null || modelIRPackageStores != null || modelIRCredential != null) {
            modelIRSSISProperties = {
                catalogInfo: modelIRSSISCatalogInfo,
                customSetupScriptProperties: modelIRCustomSetupScriptProperties,
                edition: ir.skuSetting,
                runtimeVersion: ecSvc.enableSsisIrVNetV2Feature ? ir.runtimeVersion : undefined,
                licenseType: ir.licenseType,
                dataProxyProperties: modelIRDataProxyProperties,
                expressCustomSetupProperties: modelIRCustomSetups,
                packageStores: modelIRPackageStores,
                credential: modelIRCredential
            };
        }

        if (!modelIRSSISProperties && modelIRComputeProperties != null) {
            if (!ir.dataFlowTimeToLiveInMinutes) {
                modelIRComputeProperties.dataFlowProperties = {
                    computeType: ir.dataFlowComputeType,
                    coreCount: ir.dataFlowCoreCount,
                    timeToLive: ir.dataFlowTimeToLive,
                    cleanup: ir.dataFlowCleanup,
                    customProperties: customPropertiesLocal,
                    externalComputeInfo: ir.externalComputeInfo
                };
            } else {
                modelIRComputeProperties.dataFlowProperties = {
                    computeType: ir.dataFlowComputeType,
                    coreCount: ir.dataFlowCoreCount,
                    timeToLiveInMinutes: ir.dataFlowTimeToLiveInMinutes,
                    cleanup: ir.dataFlowCleanup,
                    customProperties: customPropertiesLocal,
                    externalComputeInfo: ir.externalComputeInfo
                };
            }

            if (((GlobalVariables.isCmdApiApp && __IS_A365__) || GlobalVariables.isSparkGatewayEnabled)
                && ir.useSparkGateway) {
                modelIRComputeProperties.sparkProperties = {
                    useSparkGateway: ir.useSparkGateway
                };
            }

            if (ir.managedVirtualNetwork != null) {
                if (ir.copyComputeScaleEnabled) {
                    modelIRComputeProperties.copyComputeScaleProperties = {
                        dataIntegrationUnit: ir.copyComputeScaleDataIntegrationUnit,
                        timeToLive: ir.copyComputeScaleTimeToLive
                    };
                }

                if (ir.hasPipelineExternalProperty) {
                    modelIRComputeProperties.pipelineExternalComputeScaleProperties = {
                        timeToLive: ir.pipelineExternalComputeScaleTimeToLive,
                        numberOfPipelineNodes: ir.numberOfPipelineNodes,
                        numberOfExternalNodes: ir.numberOfExternalNodes,
                    };
                }
            }
        }

        let modelIRSharedProperties: Model.IntegrationRuntimeSharingProperties | undefined;
        if (ir.linkedAzureSsisIrResourceIds != null) {
            modelIRSharedProperties = {
                linkedAzureSsisIrResourceIds: ir.linkedAzureSsisIrResourceIds
            };
        }

        let modelIRLinkedProperties: Model.IntegrationRuntimeLinkedInfo | undefined;
        if (ir.sharedIROrigianlResourceId != null) {
            modelIRLinkedProperties = {
                resourceId: ir.sharedIROrigianlResourceId,
                sharedIntegrationRuntimeLocation: ir.sharedIntegrationRuntimeLocation
            };
        }

        const modelIRTypeProperties: Model.ManagedIntegrationRuntimeTypeProperties = {
            computeProperties: modelIRComputeProperties,
            ssisProperties: modelIRSSISProperties,
            customerVirtualNetwork: modelIRCustomerVirtualNetwork,
            linkedInfo: modelIRLinkedProperties,
            sharingProperties: modelIRSharedProperties
        };

        const modelIRProperties: Model.ManagedIntegrationRuntime = {
            type: irTypesConst.Managed,
            description: modelIR.properties ? modelIR.properties.description : undefined,
            typeProperties: modelIRTypeProperties
        };

        if (ir.managedVirtualNetwork != null) {
            modelIRProperties.managedVirtualNetwork = {
                type: 'ManagedVirtualNetworkReference',
                referenceName: ir.managedVirtualNetwork.name
            };
        }

        if (ir.linkedService != null) {
            modelIRProperties.linkedService = {
                type: 'LinkedServiceReference',
                referenceName: ir.linkedService.name
            };
        }

        modelIR.properties = modelIRProperties;
        return modelIR;
    }

    protected async _toModel(modelIRResource: Model.IntegrationRuntimeResource, integrationRuntime?: ManagedIntegrationRuntime): Promise<ManagedIntegrationRuntime> {
        if (!integrationRuntime) {
            integrationRuntime = new ManagedIntegrationRuntime();
        }

        await super._toModel(modelIRResource, integrationRuntime);

        const modelIRResourceProperties: Model.ManagedIntegrationRuntime = modelIRResource.properties as Model.ManagedIntegrationRuntime;

        if (modelIRResourceProperties.managedVirtualNetwork != null) {
            integrationRuntime.managedVirtualNetwork = ReferenceAdapter.toProxyModel<ManagedVirtualNetwork>(
                Metamodel.modelFor(ManagedVirtualNetwork),
                modelIRResourceProperties.managedVirtualNetwork, this.context);
        }

        if (modelIRResourceProperties.typeProperties.computeProperties != null) {
            integrationRuntime.computeLocation = formatRegionString(modelIRResourceProperties.typeProperties.computeProperties.location);
            integrationRuntime.computeNodeSize = modelIRResourceProperties.typeProperties.computeProperties.nodeSize;
            integrationRuntime.computeNumberOfNodes = modelIRResourceProperties.typeProperties.computeProperties.numberOfNodes;
            integrationRuntime.computeMaxParallelExecutionsPerNode = modelIRResourceProperties.typeProperties.computeProperties.maxParallelExecutionsPerNode;
            if (modelIRResourceProperties.typeProperties.computeProperties.vNetProperties != null) {
                if (modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.subnetId != null) {
                    const computeSubnetID = modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.subnetId;
                    const subnetsKeyIndex = computeSubnetID.lastIndexOf(ssisConstants.subnetsKeyInResourceId);
                    if (subnetsKeyIndex !== -1) {
                        integrationRuntime.computeVNetId = computeSubnetID.substring(0, subnetsKeyIndex);
                        integrationRuntime.computeSubnet = computeSubnetID.substring(subnetsKeyIndex + ssisConstants.subnetsKeyInResourceId.length);
                    }
                } else {
                    integrationRuntime.computeVNetId = modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.vNetId;
                    integrationRuntime.computeSubnet = modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.subnet;
                }

                if (!stringIsNullOrEmpty(integrationRuntime.computeVNetId)) {
                    const vnetResourceId = ResourceId.parseFrom(integrationRuntime.computeVNetId, 'Microsoft.Network', 'virtualNetworks');
                    if (vnetResourceId != null) {
                        integrationRuntime.vnetSubscription = vnetResourceId.subscriptionId;
                    }
                }
                if (modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.publicIPs != null) {
                    integrationRuntime.bringIpAllowed = true;
                    integrationRuntime.publicIPs = modelIRResourceProperties.typeProperties.computeProperties.vNetProperties.publicIPs;
                } else {
                    integrationRuntime.bringIpAllowed = false;
                }
            }
            if (modelIRResourceProperties.typeProperties.computeProperties.virtualNetwork != null) {
                integrationRuntime.computeSubnetResourceId = modelIRResourceProperties.typeProperties.computeProperties.virtualNetwork.subnetId;
            }

            if (!!modelIRResourceProperties.typeProperties.computeProperties.dataFlowProperties) {
                const dataFlowProperties: Model.IntegrationRuntimeComputeProperties = modelIRResourceProperties.typeProperties.computeProperties.dataFlowProperties;
                const dataFlowPropertiesCopy = Object.fromEntries(Object.entries(dataFlowProperties).map(([k, v]) => [k.toLowerCase(), v]));
                const customPropertiesLocal: Model.ICustomProperty[] = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'customProperties');
                //convert backend customproperty model to UI
                const customPropertiesUI: SharedModel.ICustomProperty[] = customPropertiesLocal != null ? customPropertiesLocal.map((prop) => { return { key: prop.name, value: prop.value }; }) : undefined;

                integrationRuntime.dataFlowComputeType = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'computeType') as ComputeType;
                integrationRuntime.dataFlowCoreCount = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'coreCount');
                integrationRuntime.dataFlowTimeToLive = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'timeToLive');
                integrationRuntime.dataFlowTimeToLiveInMinutes = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'timeToLiveInMinutes');
                integrationRuntime.dataFlowCleanup = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'cleanup');
                integrationRuntime.customProperties = customPropertiesUI;
                integrationRuntime.externalComputeInfo = this._getValueByLowerCaseKey(dataFlowPropertiesCopy, 'externalComputeInfo');
            }

            if (!!modelIRResourceProperties.typeProperties.computeProperties.copyComputeScaleProperties) {
                integrationRuntime.copyComputeScaleEnabled = true;
                integrationRuntime.copyComputeScaleDataIntegrationUnit = modelIRResourceProperties.typeProperties.computeProperties.copyComputeScaleProperties.dataIntegrationUnit;
                integrationRuntime.copyComputeScaleTimeToLive = modelIRResourceProperties.typeProperties.computeProperties.copyComputeScaleProperties.timeToLive;
            } else {
                integrationRuntime.copyComputeScaleEnabled = false;
            }

            if (!!modelIRResourceProperties.typeProperties.computeProperties.pipelineExternalComputeScaleProperties) {
                integrationRuntime.hasPipelineExternalProperty = true;
                integrationRuntime.pipelineExternalComputeScaleTimeToLive = modelIRResourceProperties.typeProperties.computeProperties.pipelineExternalComputeScaleProperties.timeToLive;
                integrationRuntime.numberOfPipelineNodes = modelIRResourceProperties.typeProperties.computeProperties.pipelineExternalComputeScaleProperties.numberOfPipelineNodes;
                integrationRuntime.numberOfExternalNodes = modelIRResourceProperties.typeProperties.computeProperties.pipelineExternalComputeScaleProperties.numberOfExternalNodes;
            } else {
                integrationRuntime.hasPipelineExternalProperty = false;
            }

            if (((GlobalVariables.isCmdApiApp && __IS_A365__) || GlobalVariables.isSparkGatewayEnabled)
                && modelIRResourceProperties.typeProperties.computeProperties.sparkProperties) {
                integrationRuntime.useSparkGateway = modelIRResourceProperties.typeProperties.computeProperties.sparkProperties.useSparkGateway;
            }
        }

        if (modelIRResourceProperties.typeProperties.customerVirtualNetwork != null) {
            const subnetId = modelIRResourceProperties.typeProperties.customerVirtualNetwork.subnetId;
            integrationRuntime.useVNetV2 = !stringIsNullOrEmpty(subnetId);
            if (integrationRuntime.useVNetV2) {
                const index = subnetId.lastIndexOf(ssisConstants.subnetsKeyInResourceId);
                if (index !== -1) {
                    integrationRuntime.computeVNetId = subnetId.substring(0, index);
                    integrationRuntime.computeSubnet = subnetId.substring(index + ssisConstants.subnetsKeyInResourceId.length);
                }
            }
        }

        if (modelIRResourceProperties.typeProperties.ssisProperties != null) {
            integrationRuntime.skuSetting = modelIRResourceProperties.typeProperties.ssisProperties.edition != null ?
                modelIRResourceProperties.typeProperties.ssisProperties.edition : ssisConstants.ssisIRSkus.Standard;
                const ecSvc = await getServiceInstance<ExposureControlService>(this.context.contextData, 'ExposureControlService');
                integrationRuntime.runtimeVersion = ecSvc.enableEvergreenSsis ?(modelIRResourceProperties.typeProperties.ssisProperties.runtimeVersion != null ?
                modelIRResourceProperties.typeProperties.ssisProperties.runtimeVersion : ssisConstants.ssisRuntimeVersion.Sql2017) : undefined;
            if (modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo != null) {
                integrationRuntime.useSSISDB = true;
                integrationRuntime.ssisCatalogServerEndpoint = modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo.catalogServerEndpoint;
                integrationRuntime.ssisCatalogAdminUsername = modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo.catalogAdminUserName;
                integrationRuntime.dualStandbyPairName = modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo.dualStandbyPairName;
                integrationRuntime.ssisCatalogAdminPassword = integrationRuntime.ssisCatalogAdminUsername != null ?
                    modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo.catalogAdminPassword.value : null;
                integrationRuntime.ssisCatalogPricingTier = modelIRResourceProperties.typeProperties.ssisProperties.catalogInfo.catalogPricingTier;
            } else {
                integrationRuntime.useSSISDB = false;
            }
            const packageStores = modelIRResourceProperties.typeProperties.ssisProperties.packageStores;
            if (packageStores != null && packageStores.length > 0) {
                integrationRuntime.usePackageStore = true;
                integrationRuntime.packageStores = [];
                packageStores.forEach((store: Model.PackageStore) => {
                    const packageStore = new PackageStore();
                    packageStore.storeName = store.name;
                    packageStore.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(LinkedService), store.packageStoreLinkedService, this.context);
                    integrationRuntime.packageStores.push(packageStore);
                });
            } else {
                integrationRuntime.usePackageStore = false;
            }
            if (modelIRResourceProperties.typeProperties.ssisProperties.customSetupScriptProperties != null) {
                if (modelIRResourceProperties.typeProperties.ssisProperties.customSetupScriptProperties.blobContainerUri != null) {
                    integrationRuntime.ssisCustomSetupContainerSasUri = modelIRResourceProperties.typeProperties.ssisProperties.customSetupScriptProperties.blobContainerUri;
                    if (modelIRResourceProperties.typeProperties.ssisProperties.customSetupScriptProperties.sasToken != null) {
                        integrationRuntime.ssisCustomSetupContainerSasUri += modelIRResourceProperties.typeProperties.ssisProperties.customSetupScriptProperties.sasToken.value;
                    }
                }
            }
            integrationRuntime.licenseType = modelIRResourceProperties.typeProperties.ssisProperties.licenseType || ssisConstants.ssisIRLicenseTypes.LicenseIncluded;

            const credential = modelIRResourceProperties.typeProperties.ssisProperties.credential;
            if (credential != null) {
                integrationRuntime.useUAMIAuth = true;
                integrationRuntime.credential = ReferenceAdapter.toProxyModel<Credential>(Metamodel.modelFor(Credential), credential, this.context);
            } else {
                integrationRuntime.useUAMIAuth = false;
            }

            integrationRuntime.useAADAuth = integrationRuntime.ssisCatalogAdminUsername == null && !integrationRuntime.useUAMIAuth;
            const dataProxyProperties = modelIRResourceProperties.typeProperties.ssisProperties.dataProxyProperties;
            if (dataProxyProperties != null) {
                integrationRuntime.ssisSelfHostedIR =
                    ReferenceAdapter.toProxyModel<SelfHostedIntegrationRuntime>(Metamodel.modelFor(SelfHostedIntegrationRuntime), dataProxyProperties.connectVia, this.context);
                integrationRuntime.ssisBlobLinkedService =
                    ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService), dataProxyProperties.stagingLinkedService, this.context);
                integrationRuntime.ssisStoragePath = dataProxyProperties.path;
            }
            const customSetups = modelIRResourceProperties.typeProperties.ssisProperties.expressCustomSetupProperties;
            if (customSetups != null && customSetups.length > 0) {
                integrationRuntime.ssisCustomSetups = [];
                customSetups.forEach((setup: Model.CustomSetupBase) => {
                    if (setup.type === expressCustomSetupTypes.Cmdkey) {
                        const modelCmdkeyExtension = setup as Model.CmdkeySetup;
                        const customSetup = new CmdkeyCommandSetup();
                        if (modelCmdkeyExtension.typeProperties) {
                            customSetup.targetName = modelCmdkeyExtension.typeProperties.targetName;
                            customSetup.userName = modelCmdkeyExtension.typeProperties.userName;
                            customSetup.password = modelCmdkeyExtension.typeProperties.password.value;
                        }
                        integrationRuntime.ssisCustomSetups.push(customSetup);
                    } else if (setup.type === expressCustomSetupTypes.EnvVariable) {
                        const modelEnvironmentVariableExtension = setup as Model.EnvironmentVariableSetup;
                        const customSetup = new EnvironmentVariableSetup();
                        if (modelEnvironmentVariableExtension.typeProperties) {
                            customSetup.variableName = modelEnvironmentVariableExtension.typeProperties.variableName;
                            customSetup.variableValue = modelEnvironmentVariableExtension.typeProperties.variableValue;
                        }
                        integrationRuntime.ssisCustomSetups.push(customSetup);
                    } else if (setup.type === expressCustomSetupTypes.Installation) {
                        const modelLicensedExtension = setup as Model.LicensedComponentSetup;
                        if (modelLicensedExtension) {
                            if (modelLicensedExtension.typeProperties && isAvailableComponent(modelLicensedExtension.typeProperties.componentName)) {
                                const customSetup = new LicensedComponentSetup();
                                customSetup.componentName = modelLicensedExtension.typeProperties.componentName;
                                customSetup.licenseKey = isLicensedComponent(modelLicensedExtension.typeProperties.componentName)
                                    ? modelLicensedExtension.typeProperties.licenseKey.value : '';
                                if (customSetup.componentName === supportedComponentNames.IntegrationToolkit || customSetup.componentName === supportedComponentNames.ProductivityPack) {
                                    customSetup.licenseKeyPattern = LicensedComponentSetup.licenseKeyKingswaySoft;
                                }
                                integrationRuntime.ssisCustomSetups.push(customSetup);
                            }
                        } else {
                            const modelExtension = setup as Model.ComponentSetup;
                            if (modelExtension && modelExtension.typeProperties && isAvailableComponent(modelExtension.typeProperties.componentName)) {
                                const customSetup = new ComponentSetup();
                                customSetup.componentName = modelExtension.typeProperties.componentName;
                                integrationRuntime.ssisCustomSetups.push(customSetup);
                            }
                        }
                    } else if (setup.type === expressCustomSetupTypes.AzPowerShell) {
                        const modelAzPowerShellExtension = setup as Model.AzPowerShellSetup;
                        const customSetup = new AzPowerShellSetup();
                        if (modelAzPowerShellExtension.typeProperties) {
                            customSetup.version = modelAzPowerShellExtension.typeProperties.version;
                        }
                        integrationRuntime.ssisCustomSetups.push(customSetup);
                    }
                });
            }
        }

        if (modelIRResourceProperties.linkedService) {
            integrationRuntime.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(LinkedService), modelIRResourceProperties.linkedService, this.context);
        }

        if (modelIRResourceProperties.typeProperties.sharingProperties != null) {
            integrationRuntime.useSharingSsisIr = true;
            integrationRuntime.linkedAzureSsisIrResourceIds = modelIRResourceProperties.typeProperties.sharingProperties.linkedAzureSsisIrResourceIds;
        }

        if (modelIRResourceProperties.typeProperties.linkedInfo != null) {
            integrationRuntime.sharedIROrigianlResourceId = modelIRResourceProperties.typeProperties.linkedInfo.resourceId;
            integrationRuntime.sharedIntegrationRuntimeLocation = modelIRResourceProperties.typeProperties.linkedInfo.sharedIntegrationRuntimeLocation;
        }

        return integrationRuntime;
    }

    private async _toModelIRComputeVNetProperties(ir: ManagedIntegrationRuntime): Promise<Model.IntegrationRuntimeVNetProperties | undefined> {

        const ecSvc = await getServiceInstance<ExposureControlService>(this.context.contextData, 'ExposureControlService');
        if (ir == null || (ecSvc.enableSsisIrVNetV2Feature && ir.useVNetV2) || stringIsNullOrEmpty(ir.computeSubnet) || stringIsNullOrEmpty(ir.computeVNetId)) {
            return undefined;
        }

        // When subnet id is present, vNetId and subnet should be ignored.
        return {
            subnetId: ir.computeVNetId + ssisConstants.subnetsKeyInResourceId + ir.computeSubnet,
            publicIPs: ir.publicIPs
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _getValueByLowerCaseKey(obj: any, key: string): any {
        return obj[key.toLowerCase()];
    }
}
